import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { selectApiUrl, selectApiUrlSV } from '../settings/settings.selectors';
import { logout } from './auth.actions';
import { IAuthState } from './auth.reducer';
import * as Sentry from '@sentry/browser';
import { ToastService } from '@qld-recreational/toast';
import { isTrustedApiUrl } from './trusted-api-url';

const sendTokenExpireSentryError = async (
  request: HttpRequest<any>,
  error: HttpErrorResponse,
) => {
  Sentry.withScope((scope) => {
    scope.setExtras({
      url: request.url,
      error,
    });
    Sentry.captureMessage('Interceptor captured error');
  });
};

const checkForExpiredToken = async (
  request: HttpRequest<any>,
  error: HttpErrorResponse,
  toastService: ToastService,
  authStore: Store<IAuthState>,
) => {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 401) {
      await sendTokenExpireSentryError(request, error);
      await toastService.presentWarningToast(
        'token expired, please login again',
      );
      authStore.dispatch(logout());
    }
  }
};

export const ExpiredTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const authStore = inject(Store);
  const settingStore = inject(Store);
  const toastService = inject(ToastService);
  return settingStore.select(selectApiUrl).pipe(
    withLatestFrom(settingStore.select(selectApiUrlSV)),
    map(([apiUrl, apiUrlSV]) =>
      isTrustedApiUrl(request.url, [apiUrl, apiUrlSV]),
    ),
    switchMap((isApiUrl) =>
      next(request).pipe(
        tap({
          next: undefined,
          error: async (error: any) => {
            if (isApiUrl && !request.url.includes('login')) {
              await checkForExpiredToken(
                request,
                error,
                toastService,
                authStore,
              );
            }
          },
        }),
      ),
    ),
  );
};
